import React, { useState, useEffect } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { studyProtocolReviewServices } from '../../APIServices/studyProtocolReviewServices';
import { reviewChecklistMetaServices } from '../../APIServices/reviewChecklistMetaServices';

const DEFAULT_SAP_SECTIONS = [
  { id: 's1', num: '1', title: 'Amendments from Previous Version', status: 'Approved', content: '' },
  { id: 's2', num: '2', title: 'Introduction', status: 'Approved', content: '' },
  {
    id: 's2.1',
    num: '2.1',
    title: 'Study Design',
    status: 'Draft',
    content: 'This is a randomised, open-label, balanced, two-treatment, two-period, two-sequence, single-dose crossover bioequivalence study. Subjects will be randomised to one of two treatment sequences (TR or RT) in a 1:1 ratio. The study will be conducted under fasting conditions with a minimum 7-day washout period between doses.'
  },
  { id: 's2.2', num: '2.2', title: 'Study Objectives', status: 'Draft', content: '' },
  { id: 's3', num: '3', title: 'Interim & Final Analysis', status: 'Draft', content: '' },
  { id: 's4.1', num: '4.1', title: 'Statistical Hypotheses', status: 'Approved', content: '' },
  { id: 's4.2', num: '4.2', title: 'Statistical Decision Rules', status: 'Draft', content: '' },
  { id: 's5.1', num: '5.1', title: 'Full Analysis Set', status: 'Draft', content: '' },
  { id: 's5.2', num: '5.2', title: 'Per Protocol Set', status: 'Draft', content: '' },
  { id: 's5.3', num: '5.3', title: 'Safety Analysis Set', status: 'Draft', content: '' },
  { id: 's6', num: '6', title: 'Primary & Secondary Endpoints', status: 'Approved', content: '' },
  { id: 's7', num: '7', title: 'Handling of Missing Values', status: 'Draft', content: '' },
  { id: 's8.1', num: '8.1', title: 'Statistical Methods', status: 'Draft', content: '' },
  { id: 's9', num: '9', title: 'References', status: 'Draft', content: '' },
  { id: 's10', num: '10', title: 'Appendices', status: 'Draft', content: '' },
  { id: 's11', num: '11', title: 'Revision History', status: 'Draft', content: '' },
  { id: 's12', num: '12', title: 'SAP Approval Signature', status: 'Draft', content: '' }
];

const DEFAULT_TLF_ROWS = [
  { no: '14.1.1', type: 'Table', title: 'Subject Disposition', pop: 'All Screened', ep: 'Disposition', status: 'Planned', qc: 'Pass' },
  { no: '14.1.2', type: 'Table', title: 'Analysis Populations', pop: 'Randomized', ep: 'Populations', status: 'Planned', qc: 'Pass' },
  { no: '14.1.3', type: 'Table', title: 'Demographics and Baseline', pop: 'FAS / Safety', ep: 'Demographics', status: 'In Progress', qc: 'Pass' },
  { no: '14.1.4', type: 'Table', title: 'Protocol Deviations', pop: 'Randomized', ep: 'Deviations', status: 'Planned', qc: 'Pass' },
  { no: '14.2.1', type: 'Table', title: 'Primary Endpoints Summary', pop: 'Completer', ep: 'Cmax, AUC', status: 'Mocked', qc: 'Pass' },
  { no: '14.2.2', type: 'Table', title: 'Sensitivity Analysis', pop: 'FAS / PP', ep: 'Cmax, AUC', status: 'Planned', qc: 'Fail' },
  { no: '14.2.F1', type: 'Figure', title: 'Concentration-Time Profile', pop: 'PK Population', ep: 'Concentration', status: 'Planned', qc: 'Warn' },
  { no: '14.3.1', type: 'Table', title: 'TEAE Summary', pop: 'Safety', ep: 'TEAE', status: 'Planned', qc: 'Pass' },
  { no: '14.3.2', type: 'Table', title: 'TEAEs by SOC/PT', pop: 'Safety', ep: 'TEAE', status: 'Planned', qc: 'Fail' },
  { no: '16.2.1', type: 'Listing', title: 'Subject Disposition Listing', pop: 'All Enrolled', ep: 'Disposition', status: 'Planned', qc: 'Warn' },
  { no: '16.2.7', type: 'Listing', title: 'Adverse Event Listing', pop: 'Safety', ep: 'AE', status: 'Planned', qc: 'Pass' },
  { no: '16.2.8', type: 'Listing', title: 'PK Concentration Listing', pop: 'PK Population', ep: 'PK Concentrations', status: 'Planned', qc: 'Warn' }
];

const DEFAULT_QC_RULES = [
  { cat: 'Critical', check: 'SAP approval timing', status: 'Pass', msg: 'Approval timing documented as pre-lock.' },
  { cat: 'Critical', check: 'Primary endpoint has decision rule', status: 'Pass', msg: 'Decision rule language present.' },
  { cat: 'Critical', check: 'Estimand defined for each primary', status: 'Fail', msg: 'Estimands missing one or more ICH E9(R1) attributes.' },
  { cat: 'Major', check: 'TLF entry for each primary endpoint', status: 'Pass', msg: 'Every estimand endpoint has at least one TLF entry.' },
  { cat: 'Major', check: 'Analysis sets defined (FAS, Safety)', status: 'Pass', msg: 'FAS and Safety sets both authored.' },
  { cat: 'Major', check: 'Missing-data handling addressed', status: 'Warn', msg: 'No missing-data method keywords detected.' },
  { cat: 'Major', check: 'Regulatory references cited', status: 'Warn', msg: 'Only ICH E9 cited. Consider E9(R1) and M13A.' },
  { cat: 'Minor', check: 'Revision history maintained', status: 'Pass', msg: 'Revision history populated.' }
];

const cloneSapSections = () => DEFAULT_SAP_SECTIONS.map((s) => ({ ...s }));
const cloneTlfRows = () => DEFAULT_TLF_ROWS.map((r) => ({ ...r }));
const cloneQcRules = () => DEFAULT_QC_RULES.map((r) => ({ ...r }));

function CSRReviewModule() {
  const { state, audit, currentUser } = useData();
  const toast = useToast();
  
  const [activeTab, setActiveTab] = useState('upload');
  const [projectId, setProjectId] = useState(null);
  const [csr, setCsr] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [csrText, setCsrText] = useState('');
  const [fileName, setFileName] = useState('');
  const [headlineNumbers, setHeadlineNumbers] = useState({});
  const [responses, setResponses] = useState({});
  const [score, setScore] = useState({ applicable: 0, reviewed: 0, pct: 0, openCrit: 0, openMajor: 0, conclusion: 'Pending' });
  const [csrChecklistMeta, setCsrChecklistMeta] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [checklistFilter, setChecklistFilter] = useState({
    severity: 'all',
    compliance: 'all',
    status: 'all',
    search: ''
  });
  const [sapSections, setSapSections] = useState(cloneSapSections());
  const [selectedSapSectionId, setSelectedSapSectionId] = useState('s2.1');
  const [tlfRows, setTlfRows] = useState(cloneTlfRows());
  const [qcRules, setQcRules] = useState(cloneQcRules());

  // 5 Tabs as per the image
  const tabs = [
    { id: 'upload', label: 'CSR Upload', icon: 'ti-upload' },
    { id: 'checklist', label: 'CSR Checklist', icon: 'ti-checklist' },
    { id: 'report', label: 'CSR Report', icon: 'ti-file-text' },
    { id: 'sap', label: 'SAP Authoring', icon: 'ti-file-description' },
    { id: 'tlf', label: 'SAP TLF & QC', icon: 'ti-table' }
  ];

  useEffect(() => {
    setClients(state.clients || []);
    setProjects(state.projects || []);
  }, [state]);

  useEffect(() => {
    const loadChecklistMeta = async () => {
      const data = await reviewChecklistMetaServices.getReviewChecklistData('active', 'csr_review', '');
      setCsrChecklistMeta(data?.data || []);
    };

    loadChecklistMeta();
  }, []);

  useEffect(() => {
    const loadSavedReview = async () => {
      if (!projectId) {
        setCsr(null);
        setCsrText('');
        setFileName('');
        setHeadlineNumbers({});
        setResponses({});
        setScore({ applicable: 0, reviewed: 0, pct: 0, openCrit: 0, openMajor: 0, conclusion: 'Pending' });
        setSapSections(cloneSapSections());
        setSelectedSapSectionId('s2.1');
        setTlfRows(cloneTlfRows());
        setQcRules(cloneQcRules());
        return;
      }

      const data = await studyProtocolReviewServices.getStudyProtocolReview('csr_review', 'active', projectId);
      const row = data?.data?.[0];

      if (!row) {
        setCsr(null);
        setCsrText('');
        setFileName('');
        setHeadlineNumbers({});
        setResponses({});
        setScore({ applicable: 0, reviewed: 0, pct: 0, openCrit: 0, openMajor: 0, conclusion: 'Pending' });
        setSapSections(cloneSapSections());
        setSelectedSapSectionId('s2.1');
        setTlfRows(cloneTlfRows());
        setQcRules(cloneQcRules());
        return;
      }

      setCsr(row);
      setCsrText(row.extractedText || '');
      setFileName(row.fileName || '');
      setHeadlineNumbers(row.headlineNumbers || {});
      setResponses(row.responses || {});
      setScore(row.score || { applicable: 0, reviewed: 0, pct: 0, openCrit: 0, openMajor: 0, conclusion: 'Pending' });
      setSapSections(Array.isArray(row.mappedSections?.sapSections) && row.mappedSections.sapSections.length > 0 ? row.mappedSections.sapSections : cloneSapSections());
      setSelectedSapSectionId((Array.isArray(row.mappedSections?.sapSections) && row.mappedSections.sapSections[0]?.id) || 's2.1');
      setTlfRows(Array.isArray(row.mappedSections?.tlfRows) && row.mappedSections.tlfRows.length > 0 ? row.mappedSections.tlfRows : cloneTlfRows());
      setQcRules(Array.isArray(row.mappedSections?.qcRules) && row.mappedSections.qcRules.length > 0 ? row.mappedSections.qcRules : cloneQcRules());
    };

    loadSavedReview();
  }, [projectId]);

  const handleCsrUpload = async (file) => {
    try {
      const text = await file.text();
      setCsrText(text);
      setFileName(file.name);
      toast('CSR uploaded successfully', 'ok');
      audit('CSR_UPLOAD', file.name, 'CSR uploaded for review', { projectId });
      
      const headline = extractHeadlineNumbers(text);
      setHeadlineNumbers(headline);

      if (projectId) {
        const payload = {
          id: csr?._id,
          reviewId: csr?.reviewId,
          reviewType: 'csr_review',
          projectId,
          fileName: file.name,
          extractedText: text,
          headlineNumbers: headline,
          responses,
          score,
          mappedSections: {
            sapSections,
            tlfRows,
            qcRules
          },
          updatedAt: new Date().toISOString()
        };

        const data = csr?._id
          ? await studyProtocolReviewServices.updateStudyProtocolReview(payload)
          : await studyProtocolReviewServices.addNewStudyProtocolReview(payload);

        if (data?.data) {
          setCsr(data.data);
        }
      }
    } catch (error) {
      toast('Error uploading CSR: ' + error.message, 'error');
    }
  };

  const extractHeadlineNumbers = (text) => {
    const patterns = {
      cmaxRatio: /Cmax[^.]{0,50}(\d{2,3}\.\d{1,2})\s*%/i,
      cmaxCiLo: /Cmax[^.]{0,200}(\d{2,3}\.\d{1,2})\s*[–-]\s*(\d{2,3}\.\d{1,2})/i,
      cmaxCiHi: /Cmax[^.]{0,200}\d{2,3}\.\d{1,2}\s*[–-]\s*(\d{2,3}\.\d{1,2})/i,
      cmaxCv: /Cmax[^.]{0,50}CV\s*[=:]\s*(\d{1,2}\.\d{1,2})\s*%/i,
      auctRatio: /AUC0-t[^.]{0,50}(\d{2,3}\.\d{1,2})\s*%/i,
      auctCiLo: /AUC0-t[^.]{0,200}(\d{2,3}\.\d{1,2})\s*[–-]\s*(\d{2,3}\.\d{1,2})/i,
      auctCiHi: /AUC0-t[^.]{0,200}\d{2,3}\.\d{1,2}\s*[–-]\s*(\d{2,3}\.\d{1,2})/i,
      auctCv: /AUC0-t[^.]{0,50}CV\s*[=:]\s*(\d{1,2}\.\d{1,2})\s*%/i,
      completerN: /completer[^.]{0,30}(\d{1,3})/i,
      safetyN: /safety[^.]{0,30}(\d{1,3})/i,
      randN: /randomi[sz]ed[^.]{0,30}(\d{1,3})/i
    };
    const result = {};
    Object.entries(patterns).forEach(([key, pattern]) => {
      const match = text.match(pattern);
      if (match) {
        result[key] = match[1] || match[0];
      }
    });
    return result;
  };

  const saveCsr = async () => {
    if (!projectId) {
      toast('Please select a project first', 'warn');
      return;
    }
    const items = getChecklistItems();
    let reviewed = 0;
    let openCrit = 0;
    let openMajor = 0;
    items.forEach((item) => {
      const r = responses[item.id];
      if (r?.compliance) {
        reviewed++;
        if (r.findingStatus === 'Open') {
          if (item.sev === 'Critical') openCrit++;
          if (item.sev === 'Major') openMajor++;
        }
      }
    });
    const applicable = items.length;
    const pct = applicable > 0 ? Math.round((reviewed / applicable) * 100) : 0;
    let conclusion = 'Pending review';
    if (openCrit > 0) conclusion = 'Critical revision required';
    else if (openMajor > 0) conclusion = 'Major revision required';
    else if (pct === 100 && reviewed === applicable) conclusion = 'Acceptable';
    else if (pct >= 90) conclusion = 'Acceptable with minor comments';
    else if (pct > 0) conclusion = 'Revision recommended';

    const liveScore = { applicable, reviewed, pct, openCrit, openMajor, conclusion };
    setScore(liveScore);

    const payload = {
      id: csr?._id,
      reviewId: csr?.reviewId,
      reviewType: 'csr_review',
      projectId,
      fileName,
      extractedText: csrText,
      headlineNumbers,
      responses,
      score: liveScore,
      mappedSections: {
        sapSections,
        tlfRows,
        qcRules
      },
      updatedAt: new Date().toISOString()
    };

    const data = csr?._id
      ? await studyProtocolReviewServices.updateStudyProtocolReview(payload)
      : await studyProtocolReviewServices.addNewStudyProtocolReview(payload);

    if (data?.data) {
      setCsr(data.data);
    }

    audit('CSR_SAVE', fileName, 'CSR saved', { projectId });
    toast('CSR saved successfully', 'ok');
  };

  const saveChecklist = async () => {
    await saveCsr();
    audit('CSR_CHECKLIST_SAVE', fileName || 'Checklist', 'CSR checklist saved', { projectId });
  };

  const saveReport = async () => {
    await saveCsr();
    audit('CSR_REPORT_SAVE', fileName || 'Report', 'CSR report saved', { projectId });
  };

  const saveSap = async () => {
    await saveCsr();
    audit('CSR_SAP_SAVE', fileName || 'SAP', 'CSR SAP saved', { projectId });
  };

  const saveTlf = async () => {
    await saveCsr();
    audit('CSR_TLF_SAVE', fileName || 'TLF', 'CSR TLF saved', { projectId });
  };

  const updateResponse = (itemId, field, value) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }));
  };

  const clearChecklistFilters = () => {
    setChecklistFilter({ severity: 'all', compliance: 'all', status: 'all', search: '' });
  };

  const getFilteredChecklistItems = (items) => {
    const search = checklistFilter.search.trim().toLowerCase();
    return items.filter((item) => {
      const r = responses[item.id] || {};
      const severityOk = checklistFilter.severity === 'all' || item.sev === checklistFilter.severity;
      const complianceOk = checklistFilter.compliance === 'all' || (r.compliance || 'blank') === checklistFilter.compliance;
      const statusOk = checklistFilter.status === 'all' || (r.findingStatus || 'Open') === checklistFilter.status;
      const searchOk =
        !search ||
        String(item.id).toLowerCase().includes(search) ||
        item.cat.toLowerCase().includes(search) ||
        item.point.toLowerCase().includes(search);
      return severityOk && complianceOk && statusOk && searchOk;
    });
  };

  const escapeHtml = (value) => {
    const str = String(value || '');
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const buildReportHtml = () => {
    const selectedProject = projects.find((p) => p.id === projectId);
    const selectedClient = clients.find((c) => c.id === selectedProject?.clientId);
    const findings = getChecklistItems().filter((item) => {
      const r = responses[item.id];
      return r?.compliance === 'No' && (item.sev === 'Critical' || item.sev === 'Major');
    });
    const recommendations = getChecklistItems().filter((item) => {
      const r = responses[item.id];
      return r?.compliance === 'No';
    }).slice(0, 5);

    const findingsRows = findings.length > 0
      ? findings.map((item, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(item.cat)}</td>
          <td>${escapeHtml(item.point)}</td>
          <td>${escapeHtml(item.sev)}</td>
          <td>${escapeHtml(`Verify and address: ${item.point}`)}</td>
          <td>Open</td>
        </tr>
      `).join('')
      : '<tr><td colspan="6" style="text-align:center; padding:10px;">No Critical or Major findings</td></tr>';

    const recommendationRows = recommendations.length > 0
      ? recommendations.map((item) => `<li><strong>${escapeHtml(item.cat)}:</strong> ${escapeHtml(item.point)}</li>`).join('')
      : '<li>No recommendations - all checks passed</li>';

    return `
      <div>
        <h1 style="margin-bottom: 8px;">CSR Statistical Review Report</h1>
        <div style="margin-bottom: 12px;"><strong>Project:</strong> ${escapeHtml(selectedProject?.name || 'Untitled Project')}</div>
        <div style="margin-bottom: 4px;"><strong>Protocol Number:</strong> ${escapeHtml(selectedProject?.protocolNumber || '-')}</div>
        <div style="margin-bottom: 4px;"><strong>Client:</strong> ${escapeHtml(selectedClient?.name || '-')}</div>
        <div style="margin-bottom: 4px;"><strong>Reviewer:</strong> ${escapeHtml(currentUser?.name || '-')}</div>
        <div style="margin-bottom: 16px;"><strong>Generated:</strong> ${escapeHtml(new Date().toLocaleString())}</div>

        <h2>1. Executive Summary</h2>
        <p><strong>Conclusion:</strong> ${escapeHtml(score.conclusion)}</p>
        <p><strong>Compliance Score:</strong> ${escapeHtml(score.pct)}%</p>
        <p><strong>Open Critical:</strong> ${escapeHtml(score.openCrit)} | <strong>Open Major:</strong> ${escapeHtml(score.openMajor)}</p>

        <h2>2. Headline Statistical Results</h2>
        <table border="1" cellspacing="0" cellpadding="6" style="border-collapse: collapse; width: 100%; font-size: 12px;">
          <thead>
            <tr>
              <th>PK Parameter</th>
              <th>T/R Ratio (%)</th>
              <th>90% CI (%)</th>
              <th>Intra-CV (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cmax</td>
              <td>${escapeHtml(headlineNumbers.cmaxRatio || '-')}</td>
              <td>${escapeHtml(headlineNumbers.cmaxCiLo || '-')} - ${escapeHtml(headlineNumbers.cmaxCiHi || '-')}</td>
              <td>${escapeHtml(headlineNumbers.cmaxCv || '-')}</td>
            </tr>
            <tr>
              <td>AUC0-t</td>
              <td>${escapeHtml(headlineNumbers.auctRatio || '-')}</td>
              <td>${escapeHtml(headlineNumbers.auctCiLo || '-')} - ${escapeHtml(headlineNumbers.auctCiHi || '-')}</td>
              <td>${escapeHtml(headlineNumbers.auctCv || '-')}</td>
            </tr>
          </tbody>
        </table>

        <h2>3. Critical & Major Findings</h2>
        <table border="1" cellspacing="0" cellpadding="6" style="border-collapse: collapse; width: 100%; font-size: 12px;">
          <thead>
            <tr>
              <th>#</th>
              <th>Category</th>
              <th>Issue Identified</th>
              <th>Severity</th>
              <th>Required Action</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${findingsRows}
          </tbody>
        </table>

        <h2>4. Recommendations</h2>
        <ol>
          ${recommendationRows}
        </ol>
      </div>
    `;
  };

  const exportReportPdf = () => {
    const reportHtml = buildReportHtml();
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      toast('Popup blocked. Please allow popups to export PDF.', 'warn');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>CSR Review Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; line-height: 1.45; }
            h1, h2, h3 { margin: 0 0 8px; }
            h2 { margin-top: 20px; }
            p { margin: 0 0 8px; }
            table { margin-top: 8px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>${reportHtml}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    audit('CSR_REPORT_EXPORT_PDF', fileName || 'CSR report', 'CSR report exported as PDF', { projectId });
    toast('Report opened for PDF export', 'ok');
  };

  const exportReportDoc = () => {
    const reportHtml = buildReportHtml();
    const docContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8" />
          <title>CSR Review Report</title>
        </head>
        <body>${reportHtml}</body>
      </html>
    `;

    const blob = new Blob(['\ufeff', docContent], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const selectedProject = projects.find((p) => p.id === projectId);
    const safeName = (selectedProject?.name || 'csr-review-report').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    audit('CSR_REPORT_EXPORT_DOC', fileName || 'CSR report', 'CSR report exported as DOC', { projectId });
    toast('DOC export completed', 'ok');
  };

  const updateSapSection = (sectionId, field, value) => {
    setSapSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, [field]: value } : s)));
  };

  const saveAndNextSapSection = async () => {
    const currentIndex = sapSections.findIndex((s) => s.id === selectedSapSectionId);
    const nextIndex = currentIndex >= 0 && currentIndex < sapSections.length - 1 ? currentIndex + 1 : currentIndex;
    await saveSap();
    if (nextIndex >= 0 && sapSections[nextIndex]) {
      setSelectedSapSectionId(sapSections[nextIndex].id);
    }
  };

  const updateTlfRow = (index, field, value) => {
    setTlfRows((prev) => prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)));
  };

  const addTlfRow = () => {
    setTlfRows((prev) => ([
      ...prev,
      { no: '', type: 'Table', title: '', pop: '', ep: '', status: 'Planned', qc: 'Warn' }
    ]));
  };

  const exportTlfCsv = () => {
    const headers = ['No', 'Type', 'Title', 'Population', 'Endpoint', 'Status', 'QC'];
    const rows = tlfRows.map((row) => [row.no, row.type, row.title, row.pop, row.ep, row.status, row.qc]);
    const csv = [headers, ...rows]
      .map((line) => line.map((v) => `"${String(v || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const selectedProject = projects.find((p) => p.id === projectId);
    const safeName = (selectedProject?.name || 'csr-tlf').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    audit('CSR_TLF_EXPORT_CSV', fileName || 'TLF', 'CSR TLF exported as CSV', { projectId });
    toast('TLF CSV export completed', 'ok');
  };

  const updateQcRule = (index, field, value) => {
    setQcRules((prev) => prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)));
  };

  const getChecklistItems = () => [
    ...(csrChecklistMeta.length > 0
      ? csrChecklistMeta.map((item, index) => ({
        id: item.reviewChecklistId || item._id || String(index + 1),
        cat: item.category || 'General',
        point: item.checkpoint || '',
        sev: item.severity || 'Major',
      }))
      : [
    { id: 1, cat: 'SAP & Protocol Alignment', point: 'Finalised SAP exists and is version-controlled', sev: 'Critical' },
    { id: 2, cat: 'SAP & Protocol Alignment', point: 'SAP referenced in CSR', sev: 'Major' },
    { id: 3, cat: 'SAP & Protocol Alignment', point: 'SAP consistent with protocol', sev: 'Critical' },
    { id: 4, cat: 'SAP & Protocol Alignment', point: 'All planned analyses performed', sev: 'Major' },
    { id: 5, cat: 'Sample-Size Determination', point: 'Sample-size assumptions clearly stated', sev: 'Critical' },
    { id: 6, cat: 'Sample-Size Determination', point: 'Sample-size formula / software referenced', sev: 'Major' },
    { id: 7, cat: 'Sample-Size Determination', point: 'Calculated minimum N reproducible', sev: 'Critical' },
    { id: 8, cat: 'Randomization', point: 'Randomization method described', sev: 'Major' },
    { id: 9, cat: 'Randomization', point: 'Seed value recorded', sev: 'Major' },
    { id: 10, cat: 'Populations & Disposition', point: 'Safety population defined', sev: 'Critical' },
    { id: 11, cat: 'Populations & Disposition', point: 'PK population defined', sev: 'Critical' },
    { id: 12, cat: 'Populations & Disposition', point: 'Completer (BE) population defined', sev: 'Critical' },
    { id: 13, cat: 'Missing Data & Deviations', point: 'Missing-sample rules pre-specified', sev: 'Critical' },
    { id: 14, cat: 'Missing Data & Deviations', point: 'No imputation for missing concentrations', sev: 'Critical' },
    { id: 15, cat: 'Missing Data & Deviations', point: 'Pre-dose concentration rule applied', sev: 'Critical' },
    { id: 16, cat: 'Descriptive Statistics', point: 'Descriptive statistics complete', sev: 'Major' },
    { id: 17, cat: 'Descriptive Statistics', point: 'Geometric mean reported for Cmax, AUC', sev: 'Major' },
    { id: 18, cat: 'ANOVA Model', point: 'Log-transformation applied', sev: 'Critical' },
    { id: 19, cat: 'ANOVA Model', point: 'Fixed effects correctly specified', sev: 'Critical' },
    { id: 20, cat: 'ANOVA Model', point: 'Subject nested within sequence specified', sev: 'Critical' },
    { id: 21, cat: '90% CI Estimation', point: 'LSM difference computed on ln scale', sev: 'Critical' },
    { id: 22, cat: '90% CI Estimation', point: 'Anti-log applied to obtain ratio', sev: 'Critical' },
    { id: 23, cat: '90% CI Estimation', point: 'Cmax 90% CI within 80.00–125.00%', sev: 'Critical' },
    { id: 24, cat: '90% CI Estimation', point: 'AUC0-t 90% CI within 80.00–125.00%', sev: 'Critical' },
    { id: 25, cat: 'BE Conclusion', point: 'BE decision rule pre-specified', sev: 'Critical' },
    { id: 26, cat: 'BE Conclusion', point: 'BE criterion met for Cmax', sev: 'Critical' },
    { id: 27, cat: 'BE Conclusion', point: 'BE criterion met for AUC0-t', sev: 'Critical' },
    { id: 28, cat: 'Software & Reproducibility', point: 'Software version locked & validated', sev: 'Critical' },
    { id: 29, cat: 'Software & Reproducibility', point: 'Audit trail intact', sev: 'Critical' },
    { id: 30, cat: 'Software & Reproducibility', point: '21 CFR Part 11 compliance', sev: 'Critical' },
    { id: 31, cat: 'Regulatory Compliance', point: 'ICH M13A compliance', sev: 'Critical' },
    { id: 32, cat: 'Regulatory Compliance', point: 'ICH E9 / E9(R1) statistical principles', sev: 'Major' },
    { id: 33, cat: 'Regulatory Compliance', point: 'ICH E3 CSR structure compliance', sev: 'Major' },
    { id: 34, cat: 'Regulatory Compliance', point: 'FDA BE statistical guidance', sev: 'Major' },
    { id: 35, cat: 'Internal Consistency', point: 'Synopsis descriptive table = Body table', sev: 'Critical' },
    { id: 36, cat: 'Internal Consistency', point: 'Synopsis BE summary = Body BE summary', sev: 'Critical' }
  ])
  ];

  const computeScore = () => {
    const items = getChecklistItems();
    let reviewed = 0, applicable = items.length, openCrit = 0, openMajor = 0;
    items.forEach(item => {
      const r = responses[item.id];
      if (r?.compliance) {
        reviewed++;
        if (r.findingStatus === 'Open') {
          if (item.sev === 'Critical') openCrit++;
          if (item.sev === 'Major') openMajor++;
        }
      }
    });
    const pct = applicable > 0 ? Math.round((reviewed / applicable) * 100) : 0;
    let conclusion = 'Pending review';
    if (openCrit > 0) conclusion = 'Critical revision required';
    else if (openMajor > 0) conclusion = 'Major revision required';
    else if (pct === 100 && reviewed === applicable) conclusion = 'Acceptable';
    else if (pct >= 90) conclusion = 'Acceptable with minor comments';
    else if (pct > 0) conclusion = 'Revision recommended';
    
    setScore({ applicable, reviewed, pct, openCrit, openMajor, conclusion });
  };

  useEffect(() => {
    computeScore();
  }, [responses]);

  // ==================== TAB 1: CSR Upload ====================
  const renderUploadTab = () => (
    <div>
      <div className="card card-pad mb-4">
        <div className="card-title">
          <i className="ti ti-cloud-upload" /> Upload CSR
        </div>
        <div className="field">
          <label className="field-label">Select Project</label>
          <select 
            className="inp" 
            value={projectId || ''} 
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">— Select a project —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name || p.code}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Upload CSR File</label>
          <input 
            type="file" 
            className="inp" 
            accept=".pdf,.docx,.txt,.html"
            onChange={(e) => {
              if (e.target.files[0]) handleCsrUpload(e.target.files[0]);
            }}
          />
          <div className="field-hint">Supported: PDF, DOCX, TXT, HTML</div>
        </div>
        {fileName && (
          <div className="alert info" style={{ marginTop: 8, padding: 10, borderRadius: 6, background: 'var(--accent-soft)', border: '1px solid var(--accent-3)' }}>
            <i className="ti ti-file" /> {fileName} — {csrText.length} characters extracted
          </div>
        )}
      </div>

      <div className="card card-pad">
        <div className="card-title">
          <i className="ti ti-file-text" /> Extracted CSR Text
        </div>
        <textarea
          className="ta"
          value={csrText}
          onChange={(e) => setCsrText(e.target.value)}
            style={{ minHeight: 200, width: "100%", padding: 20 }}
          placeholder="Upload a file or paste CSR text here..."
        />
        <div className="tiny muted mt-2">{csrText.length} characters</div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-primary" onClick={saveCsr}>
            <i className="ti ti-save" /> Save CSR
          </button>
        </div>
      </div>

      <div className="card card-pad mt-4">
        <div className="card-title">
          <i className="ti ti-chart-bar" /> Headline Statistical Numbers
        </div>
        <div className="grid-3">
          {Object.entries(headlineNumbers).map(([key, value]) => (
            <div key={key} className="field">
              <label className="field-label">{key.replace(/([A-Z])/g, ' $1').trim()}</label>
              <input 
                className="inp" 
                value={value || ''} 
                onChange={(e) => setHeadlineNumbers({ ...headlineNumbers, [key]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ==================== TAB 2: CSR Checklist ====================
  const renderChecklistTab = () => {
    const items = getChecklistItems();
    const filteredItems = getFilteredChecklistItems(items);

    return (
      <div>
        <div className="flex between mb-3">
          <div className="card-title">
            <i className="ti ti-checklist" /> CSR Checklist
            <span className="tiny muted" style={{ marginLeft: 8 }}>{filteredItems.length} of {items.length} items</span>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-sm btn-ghost" onClick={() => setShowFilters((v) => !v)}>
              <i className="ti ti-filter" /> Filter
            </button>
            <button className="btn btn-sm btn-primary" onClick={() => {
              items.forEach(item => {
                updateResponse(item.id, 'compliance', 'Yes');
                updateResponse(item.id, 'findingStatus', 'Resolved');
              });
              toast('All items marked as compliant', 'ok');
            }}>
              <i className="ti ti-check" /> Validate All
            </button>
            <button className="btn btn-sm btn-primary" onClick={saveChecklist}>
              <i className="ti ti-device-floppy" /> Save Checklist
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="card card-pad mb-3">
            <div className="grid-3" style={{ gap: 12 }}>
              <div className="field">
                <label className="field-label">Search</label>
                <input
                  className="inp"
                  value={checklistFilter.search}
                  onChange={(e) => setChecklistFilter((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Category or checkpoint"
                />
              </div>
              <div className="field">
                <label className="field-label">Severity</label>
                <select
                  className="inp"
                  value={checklistFilter.severity}
                  onChange={(e) => setChecklistFilter((prev) => ({ ...prev, severity: e.target.value }))}
                >
                  <option value="all">All</option>
                  <option value="Critical">Critical</option>
                  <option value="Major">Major</option>
                  <option value="Minor">Minor</option>
                </select>
              </div>
              <div className="field">
                <label className="field-label">Compliance</label>
                <select
                  className="inp"
                  value={checklistFilter.compliance}
                  onChange={(e) => setChecklistFilter((prev) => ({ ...prev, compliance: e.target.value }))}
                >
                  <option value="all">All</option>
                  <option value="blank">Not Reviewed</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Partial">Partial</option>
                  <option value="Not Applicable">N/A</option>
                </select>
              </div>
              <div className="field">
                <label className="field-label">Status</label>
                <select
                  className="inp"
                  value={checklistFilter.status}
                  onChange={(e) => setChecklistFilter((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="all">All</option>
                  <option value="Open">Open</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Deferred">Deferred</option>
                  <option value="Not Applicable">N/A</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2" style={{ marginTop: 8 }}>
              <button className="btn btn-sm btn-ghost" onClick={clearChecklistFilters}>
                <i className="ti ti-eraser" /> Clear
              </button>
            </div>
          </div>
        )}

        <div className="score-banner" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          background: 'var(--surface-2)',
          padding: 16,
          borderRadius: 8,
          marginBottom: 16
        }}>
          <div>
            <div className="tiny muted">Applicable</div>
            <div className="metric-value" style={{ fontSize: 24 }}>{score.applicable}</div>
          </div>
          <div>
            <div className="tiny muted">Reviewed</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--ok)' }}>{score.reviewed}</div>
          </div>
          <div>
            <div className="tiny muted">Compliance</div>
            <div className="metric-value" style={{ fontSize: 24 }}>{score.pct}%</div>
          </div>
          <div>
            <div className="tiny muted">Open Critical</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--danger)' }}>{score.openCrit}</div>
          </div>
          <div>
            <div className="tiny muted">Open Major</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--warn)' }}>{score.openMajor}</div>
          </div>
          <div>
            <div className="tiny muted">Conclusion</div>
            <div className="metric-value" style={{ fontSize: 16 }}>{score.conclusion}</div>
          </div>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Category</th>
                <th>Checkpoint</th>
                <th style={{ width: 100 }}>Severity</th>
                <th style={{ width: 120 }}>Compliance</th>
                <th style={{ width: 120 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const r = responses[item.id] || {};
                return (
                  <tr key={item.id} className={item.sev === 'Critical' ? 'row-crit' : item.sev === 'Major' ? 'row-major' : ''}>
                    <td className="num">{item.id}</td>
                    <td>{item.cat}</td>
                    <td>{item.point}</td>
                    <td><span className={`badge ${item.sev === 'Critical' ? 'crit' : item.sev === 'Major' ? 'major' : 'minor'}`}>{item.sev}</span></td>
                    <td>
                      <select 
                        className="btn btn-xs btn-ghost"
                        value={r.compliance || ''}
                        onChange={(e) => updateResponse(item.id, 'compliance', e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="Partial">Partial</option>
                        <option value="Not Applicable">N/A</option>
                      </select>
                    </td>
                    <td>
                      <select 
                        className="btn btn-xs btn-ghost"
                        value={r.findingStatus || 'Open'}
                        onChange={(e) => updateResponse(item.id, 'findingStatus', e.target.value)}
                      >
                        <option value="Open">Open</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Deferred">Deferred</option>
                        <option value="Not Applicable">N/A</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
              {filteredItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                    No checklist item matches selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ==================== TAB 3: CSR Report ====================
  const renderReportTab = () => (
    <div>
      <div className="flex between mb-3">
        <div className="card-title">
          <i className="ti ti-file-text" /> CSR Review Report
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={exportReportPdf}>
            <i className="ti ti-download" /> Export PDF
          </button>
          <button className="btn btn-sm btn-ghost" onClick={exportReportDoc}>
            <i className="ti ti-file-type-doc" /> Export DOC
          </button>
          <button className="btn btn-sm btn-primary" onClick={saveReport}>
            <i className="ti ti-save" /> Save Report
          </button>
        </div>
      </div>

      <div className="report-paper draft card-pad">
        <div className="cover">
          <div className="deck">CSR Statistical Review Report</div>
          <h1>{projects.find(p => p.id === projectId)?.name || 'Untitled Project'}</h1>
          <div className="muted" style={{ marginTop: 8 }}>Protocol Number: {projects.find(p => p.id === projectId)?.protocolNumber || '—'}</div>
          <div className="meta-grid" style={{ marginTop: 24 }}>
            <div><div className="k">Client</div><div className="v">{clients.find(c => c.id === projects.find(p => p.id === projectId)?.clientId)?.name || '—'}</div></div>
            <div><div className="k">Reviewer</div><div className="v">{currentUser?.name || '—'}</div></div>
            <div><div className="k">Report Generated</div><div className="v">{new Date().toLocaleString()}</div></div>
            <div><div className="k">Compliance Score</div><div className="v">{score.pct}%</div></div>
          </div>
        </div>

        <h2>1. Executive Summary</h2>
        <div className={`verdict ${score.openCrit > 0 ? 'crit' : score.openMajor > 0 ? 'major' : 'ok'}`}>
          <div className="label">Overall Conclusion</div>
          <div className="text">{score.conclusion}</div>
        </div>
        <div className="meta-grid">
          <div><div className="k">Total Items</div><div className="v">{score.applicable}</div></div>
          <div><div className="k">Reviewed</div><div className="v">{score.reviewed}</div></div>
          <div><div className="k">Compliance Score</div><div className="v">{score.pct}%</div></div>
          <div><div className="k">Open Critical</div><div className="v" style={{ color: 'var(--danger)' }}>{score.openCrit}</div></div>
          <div><div className="k">Open Major</div><div className="v" style={{ color: 'var(--warn)' }}>{score.openMajor}</div></div>
        </div>

        <h2>2. Headline Statistical Results</h2>
        <table>
          <thead>
            <tr>
              <th>PK Parameter</th>
              <th>T/R Ratio (%)</th>
              <th>90% CI (%)</th>
              <th>Intra-CV (%)</th>
              <th>Pass?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Cmax</strong></td>
              <td>{headlineNumbers.cmaxRatio || '—'}</td>
              <td>{headlineNumbers.cmaxCiLo || '—'} – {headlineNumbers.cmaxCiHi || '—'}</td>
              <td>{headlineNumbers.cmaxCv || '—'}</td>
              <td><span className="badge ok">✓ Within</span></td>
            </tr>
            <tr>
              <td><strong>AUC0-t</strong></td>
              <td>{headlineNumbers.auctRatio || '—'}</td>
              <td>{headlineNumbers.auctCiLo || '—'} – {headlineNumbers.auctCiHi || '—'}</td>
              <td>{headlineNumbers.auctCv || '—'}</td>
              <td><span className="badge ok">✓ Within</span></td>
            </tr>
          </tbody>
        </table>

        <h2>3. Critical &amp; Major Findings</h2>
        <table className="tbl-findings">
          <thead>
            <tr>
              <th>#</th>
              <th>Category</th>
              <th>Issue Identified</th>
              <th>Severity</th>
              <th>Required Action</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {getChecklistItems().filter(item => {
              const r = responses[item.id];
              return r?.compliance === 'No' && (item.sev === 'Critical' || item.sev === 'Major');
            }).map((item, idx) => (
              <tr key={item.id}>
                <td>{idx + 1}</td>
                <td>{item.cat}</td>
                <td>{item.point}</td>
                <td><span className={`badge ${item.sev === 'Critical' ? 'crit' : 'major'}`}>{item.sev}</span></td>
                <td>Verify and address: {item.point}</td>
                <td><span className="badge crit">Open</span></td>
              </tr>
            ))}
            {getChecklistItems().filter(item => {
              const r = responses[item.id];
              return r?.compliance === 'No' && (item.sev === 'Critical' || item.sev === 'Major');
            }).length === 0 && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  No Critical or Major findings
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <h2>4. Recommendations</h2>
        <ol style={{ marginLeft: 22, fontSize: 13 }}>
          {getChecklistItems().filter(item => {
            const r = responses[item.id];
            return r?.compliance === 'No';
          }).slice(0, 5).map((item, idx) => (
            <li key={item.id}><strong>{item.cat}:</strong> {item.point}</li>
          ))}
          {getChecklistItems().filter(item => {
            const r = responses[item.id];
            return r?.compliance === 'No';
          }).length === 0 && (
            <li>No recommendations — all checks passed</li>
          )}
        </ol>

        <div className="sig-block">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginTop: 24 }}>
            <div>
              <div className="k">Reviewer</div>
              <div style={{ height: 48, borderBottom: '1px solid var(--ink)', margin: '8px 0' }}></div>
              <div>{currentUser?.name || '—'}<br /><span className="tiny muted">Date: {new Date().toLocaleDateString()}</span></div>
            </div>
            <div>
              <div className="k">Approved by</div>
              <div style={{ height: 48, borderBottom: '1px solid var(--ink)', margin: '8px 0' }}></div>
              <div>—<br /><span className="tiny muted">Signature &amp; Date</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== TAB 4: SAP Authoring ====================
  const renderSapTab = () => (
    <div>
      <div className="card card-pad mb-4">
        <div className="card-title">
          <i className="ti ti-file-description" /> SAP Authoring
        </div>
        <div className="alert info" style={{ padding: 12, borderRadius: 6, background: 'var(--info-soft)', border: '1px solid var(--info)', marginBottom: 12 }}>
          <i className="ti ti-info-circle" /> 
          <strong>Statistical Analysis Plan (SAP) Authoring.</strong> Use the 12-section skeleton from the template. 
          The SAP must be approved BEFORE database lock / unblinding.
        </div>

        <div className="score-banner" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
          background: 'var(--surface-2)',
          padding: 16,
          borderRadius: 8,
          marginBottom: 16
        }}>
          <div>
            <div className="tiny muted">Sections</div>
            <div className="metric-value" style={{ fontSize: 24 }}>{sapSections.filter((s) => (s.content || '').trim().length > 0).length} / {sapSections.length}</div>
          </div>
          <div>
            <div className="tiny muted">Approved</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--ok)' }}>{sapSections.filter((s) => s.status === 'Approved').length}</div>
          </div>
          <div>
            <div className="tiny muted">Estimands</div>
            <div className="metric-value" style={{ fontSize: 24 }}>{sapSections.filter((s) => /estimand/i.test(s.content || '')).length}</div>
          </div>
          <div>
            <div className="tiny muted">Open Queries</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--warn)' }}>{sapSections.filter((s) => s.status === 'Under Review').length}</div>
          </div>
        </div>

        <div className="sap-layout" style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 18 }}>
          <div className="sap-section-list" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', padding: 10, borderRadius: 6, maxHeight: 600, overflowY: 'auto' }}>
            <h4 style={{ margin: '0 0 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Template Sections</h4>
            {sapSections.map(s => (
              <div key={s.id} className="sap-sec-item" style={{ 
                padding: '7px 9px', 
                borderRadius: 4, 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8, 
                fontSize: 12,
                border: '1px solid transparent',
                marginBottom: 2,
                background: s.id === selectedSapSectionId ? '#fff' : 'transparent',
                borderColor: s.id === selectedSapSectionId ? 'var(--line)' : 'transparent',
                boxShadow: s.id === selectedSapSectionId ? '0 1px 3px rgba(0,0,0,0.04)' : 'none'
              }} onClick={() => setSelectedSapSectionId(s.id)}>
                <span className="sap-sec-num" style={{ color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600, minWidth: 28 }}>{s.num}</span>
                <span className="sap-sec-title" style={{ flex: 1, color: 'var(--ink)' }}>{s.title}</span>
                {s.status === 'Draft' && <span className="badge draft" style={{ fontSize: 9 }}>Draft</span>}
                {s.status === 'Approved' && <span className="badge ok" style={{ fontSize: 9 }}>✓</span>}
              </div>
            ))}
          </div>
          <div className="sap-section-editor" style={{ background: '#fff', border: '1px solid var(--line)', padding: 18, borderRadius: 6 }}>
            <div className="flex between" style={{ marginBottom: 14 }}>
              <h3 style={{ fontSize: 16, margin: 0 }}>{(sapSections.find((s) => s.id === selectedSapSectionId)?.num || '')} · {(sapSections.find((s) => s.id === selectedSapSectionId)?.title || 'Section')}</h3>
              <select className="btn btn-sm btn-ghost" style={{ maxWidth: 160 }} value={sapSections.find((s) => s.id === selectedSapSectionId)?.status || 'Draft'} onChange={(e) => updateSapSection(selectedSapSectionId, 'status', e.target.value)}>
                <option>Draft</option>
                <option>Under Review</option>
                <option>Approved</option>
                <option>Not Applicable</option>
              </select>
            </div>
            <div className="muted tiny" style={{ marginBottom: 10, lineHeight: 1.5 }}>
              <strong>Guidance:</strong> Brief summary of study design sufficient to understand statistical analyses. 
              Optional: schematic diagram, schedule of assessments.
            </div>
            <div className="field">
              <textarea 
                className="ta" 
                style={{ minHeight: 180 }}
                placeholder="Author the SAP text for this section. Use protocol wording where appropriate (italicise text taken directly from the protocol)."
                value={sapSections.find((s) => s.id === selectedSapSectionId)?.content || ''}
                onChange={(e) => updateSapSection(selectedSapSectionId, 'content', e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <button className="btn btn-primary" onClick={saveSap}>Save section</button>
              <button className="btn btn-ghost" onClick={saveAndNextSapSection}>Save &amp; Next →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== TAB 5: SAP TLF & QC ====================
  const renderTlfTab = () => (
    <div>
      <div className="card card-pad mb-4">
        <div className="card-title">
          <i className="ti ti-table" /> SAP TLF Shell & QC Checks
        </div>
        <div className="alert info" style={{ padding: 12, borderRadius: 6, background: 'var(--info-soft)', border: '1px solid var(--info)', marginBottom: 12 }}>
          <i className="ti ti-info-circle" /> 
          <strong>List of Tables, Listings &amp; Figures (TLF Shell).</strong> Numbering follows ICH E3 / CSR conventions.
          QC checks run against the authored SAP sections.
        </div>

        <div className="score-banner" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
          background: 'var(--surface-2)',
          padding: 16,
          borderRadius: 8,
          marginBottom: 16
        }}>
          <div>
            <div className="tiny muted">TLF Rows</div>
            <div className="metric-value" style={{ fontSize: 24 }}>{tlfRows.length}</div>
          </div>
          <div>
            <div className="tiny muted">QC Pass</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--ok)' }}>{tlfRows.filter((r) => r.qc === 'Pass').length}</div>
          </div>
          <div>
            <div className="tiny muted">QC Fail</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--danger)' }}>{tlfRows.filter((r) => r.qc === 'Fail').length}</div>
          </div>
          <div>
            <div className="tiny muted">QC Warning</div>
            <div className="metric-value" style={{ fontSize: 24, color: 'var(--warn)' }}>{tlfRows.filter((r) => r.qc === 'Warn').length}</div>
          </div>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 60 }}>No.</th>
                <th style={{ width: 60 }}>Type</th>
                <th>Title</th>
                <th>Population</th>
                <th>Endpoint</th>
                <th style={{ width: 80 }}>Status</th>
                <th style={{ width: 80 }}>QC</th>
              </tr>
            </thead>
            <tbody>
              {tlfRows.map((row, idx) => (
                <tr key={idx} className={row.qc === 'Fail' ? 'row-crit' : row.qc === 'Warn' ? 'row-major' : ''}>
                  <td className="mono"><input className="inp" value={row.no} onChange={(e) => updateTlfRow(idx, 'no', e.target.value)} /></td>
                  <td>
                    <select className="inp" value={row.type} onChange={(e) => updateTlfRow(idx, 'type', e.target.value)}>
                      <option>Table</option>
                      <option>Listing</option>
                      <option>Figure</option>
                    </select>
                  </td>
                  <td><input className="inp" value={row.title} onChange={(e) => updateTlfRow(idx, 'title', e.target.value)} /></td>
                  <td><input className="inp" value={row.pop} onChange={(e) => updateTlfRow(idx, 'pop', e.target.value)} /></td>
                  <td><input className="inp" value={row.ep} onChange={(e) => updateTlfRow(idx, 'ep', e.target.value)} /></td>
                  <td>
                    <select className="inp" value={row.status} onChange={(e) => updateTlfRow(idx, 'status', e.target.value)}>
                      <option>Planned</option>
                      <option>In Progress</option>
                      <option>Mocked</option>
                    </select>
                  </td>
                  <td>
                    <select className="inp" value={row.qc} onChange={(e) => updateTlfRow(idx, 'qc', e.target.value)}>
                      <option>Pass</option>
                      <option>Fail</option>
                      <option>Warn</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 14 }}>
          <button className="btn btn-primary" onClick={addTlfRow}>
            <i className="ti ti-plus" /> Add TLF Row
          </button>
          <button className="btn btn-ghost" style={{ marginLeft: 8 }} onClick={exportTlfCsv}>
            <i className="ti ti-download" /> Export TLF CSV
          </button>
          <button className="btn btn-primary" style={{ marginLeft: 8 }} onClick={saveTlf}>
            <i className="ti ti-device-floppy" /> Save TLF
          </button>
        </div>
      </div>

      <div className="card card-pad">
        <div className="card-title">
          <i className="ti ti-check" /> QC Rules Summary
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Category</th>
                <th>Check</th>
                <th style={{ width: 80 }}>Status</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {qcRules.map((row, idx) => (
                <tr key={idx}>
                  <td><input className="inp" value={row.cat} onChange={(e) => updateQcRule(idx, 'cat', e.target.value)} /></td>
                  <td><input className="inp" value={row.check} onChange={(e) => updateQcRule(idx, 'check', e.target.value)} /></td>
                  <td>
                    <select className="inp" value={row.status} onChange={(e) => updateQcRule(idx, 'status', e.target.value)}>
                      <option>Pass</option>
                      <option>Fail</option>
                      <option>Warn</option>
                    </select>
                  </td>
                  <td className="tiny"><input className="inp" value={row.msg} onChange={(e) => updateQcRule(idx, 'msg', e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="csr-review-module">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Study Document Review</div>
          <h1 className="page-title">CSR Review</h1>
          <p className="page-sub">Upload CSR, review checklist, generate reports, and author SAP with TLF QC.</p>
        </div>
        <div className="flex gap-2">
          <span className="tag tag-ok tag-dot">Validated</span>
          <span className="tag tag-info">ICH E3 / E9</span>
        </div>
      </div>

      <div className="tabs" style={{ 
        display: 'flex', 
        gap: 4, 
        borderBottom: '1px solid var(--line)',
        marginBottom: 24,
    }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              color: activeTab === tab.id ? 'var(--ink)' : 'var(--ink-3)',
              cursor: 'pointer',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-3)' : '2px solid transparent',
              marginBottom: -1,
              whiteSpace: 'nowrap'
            }}
          >
            <i className={`ti ${tab.icon}`} style={{ marginRight: 6 }} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'upload' && renderUploadTab()}
        {activeTab === 'checklist' && renderChecklistTab()}
        {activeTab === 'report' && renderReportTab()}
        {activeTab === 'sap' && renderSapTab()}
        {activeTab === 'tlf' && renderTlfTab()}
      </div>

      <style>{`
        .csr-review-module .row-crit td {
          background: var(--danger-soft);
        }
        .csr-review-module .row-major td {
          background: var(--warn-soft);
        }
        .csr-review-module .badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 10px;
          font-weight: 600;
          border-radius: 2px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border: 1px solid currentColor;
        }
        .csr-review-module .badge.crit {
          color: var(--danger);
          background: var(--danger-soft);
        }
        .csr-review-module .badge.major {
          color: var(--warn);
          background: var(--warn-soft);
        }
        .csr-review-module .badge.minor {
          color: var(--info);
          background: var(--info-soft);
        }
        .csr-review-module .badge.ok {
          color: var(--ok);
          background: var(--ok-soft);
        }
        .csr-review-module .badge.draft {
          color: var(--ink-3);
          background: var(--surface-2);
        }
        .csr-review-module .badge.info {
          color: var(--teal);
          background: var(--teal-soft);
        }
        .csr-review-module .report-paper {
          background: #ffffff;
          border: 1px solid var(--line);
          padding: 32px 40px;
          font-size: 13px;
          line-height: 1.65;
          color: var(--ink);
          position: relative;
          border-radius: 8px;
        }
        .csr-review-module .report-paper.draft::before {
          content: 'DRAFT';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-32deg);
          font-size: 100px;
          font-weight: 700;
          color: rgba(0,0,0,0.03);
          pointer-events: none;
          letter-spacing: -0.04em;
        }
        .csr-review-module .report-paper .cover {
          text-align: center;
          padding: 0 0 24px;
          border-bottom: 2px solid var(--ink);
          margin-bottom: 24px;
        }
        .csr-review-module .report-paper .deck {
          font-style: italic;
          color: var(--accent-3);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-size: 12px;
          margin-bottom: 12px;
        }
        .csr-review-module .report-paper .verdict {
          padding: 14px;
          border: 2px solid var(--ink);
          background: var(--surface-2);
          margin: 14px 0;
          text-align: center;
        }
        .csr-review-module .report-paper .verdict.crit {
          border-color: var(--danger);
          background: var(--danger-soft);
        }
        .csr-review-module .report-paper .verdict.major {
          border-color: var(--warn);
          background: var(--warn-soft);
        }
        .csr-review-module .report-paper .verdict.ok {
          border-color: var(--ok);
          background: var(--ok-soft);
        }
        .csr-review-module .report-paper .verdict .label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--ink-3);
        }
        .csr-review-module .report-paper .verdict .text {
          font-size: 18px;
          font-weight: 600;
          margin-top: 4px;
        }
        .csr-review-module .report-paper .meta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 24px;
          margin: 18px 0;
          font-size: 13px;
        }
        .csr-review-module .report-paper .meta-grid .k {
          color: var(--ink-3);
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .csr-review-module .report-paper .meta-grid .v {
          font-weight: 500;
        }
        .csr-review-module .report-paper .sig-block {
          margin-top: 36px;
          padding-top: 14px;
          border-top: 1px solid var(--line);
          font-size: 12px;
        }
        .csr-review-module .report-paper .sig-block .k {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--ink-3);
        }
        .csr-review-module .score-banner {
          background: var(--surface-2);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .csr-review-module .score-banner .metric-value {
          font-family: var(--display);
          font-size: 24px;
          font-weight: 500;
          line-height: 1.1;
        }
        .csr-review-module .alert.info {
          background: var(--info-soft);
          border: 1px solid var(--info);
          color: var(--info);
          padding: 12px;
          border-radius: 6px;
        }
        .csr-review-module .grid-3 {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 14px;
        }
        .csr-review-module .sap-sec-item:hover {
          background: var(--surface-2);
        }
        @media (max-width: 1024px) {
          .csr-review-module .sap-layout {
            grid-template-columns: 1fr;
          }
          .csr-review-module .sap-section-list {
            max-height: 250px;
          }
        }
        @media (max-width: 768px) {
          .csr-review-module .grid-3 {
            grid-template-columns: 1fr;
          }
          .csr-review-module .tabs {
            flex-wrap: nowrap;
            overflow-x: auto;
          }
          .csr-review-module .tab {
            font-size: 12px;
            padding: 10px 14px;
          }
        }
      `}</style>
    </div>
  );
}

export default CSRReviewModule;